import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TourOverlay } from './TourOverlay';
import { TourPopover } from './TourPopover';
import type { TourStep, TourLabels, TourCloseReason } from './types';
import './tour.css';

interface TourProps {
  steps: TourStep[];
  open: boolean;
  /**
   * Notifica el cierre del tour con la razón:
   * - `'completed'`: el usuario pulsó "¡Listo!" en el último paso.
   * - `'dismissed'`: el usuario lo cerró a medias (X del popover o click en el overlay).
   * El integrador usa la razón para persistir "completado" (check verde) vs "visto" (sin check).
   */
  onClose(reason: TourCloseReason): void;
  labels?: TourLabels;
  /**
   * Notifica el paso activo cada vez que cambia (al abrir, avanzar, retroceder o advanceOn).
   * El motor es agnóstico de la app: solo informa "ahora estás en el paso X". El consumidor
   * lo usa para sincronizar estado de la UI con el paso (p.ej. cerrar un modo edición que un
   * paso anterior abrió, al retroceder). Opcional.
   */
  onStepChange?(step: TourStep, index: number): void;
}

export type SimpleRect = { left: number; top: number; width: number; height: number };

const CUTOUT_ANIMATION_MS = 400;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function Tour({ steps, open, onClose, labels, onStepChange }: TourProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [animatedRect, setAnimatedRect] = useState<SimpleRect | null>(null);
  const rafRef = useRef<number | null>(null);

  const currentStep = steps[stepIndex];

  // Reset al primer paso cuando el tour se reabre
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // Notificar el paso activo a la app en cada cambio (apertura, Siguiente, Atrás, advanceOn).
  // Observamos el índice en vez de instrumentar cada handler: cubre todos los caminos de cambio.
  useEffect(() => {
    if (open && currentStep) onStepChange?.(currentStep, stepIndex);
  }, [open, stepIndex, currentStep, onStepChange]);

  // Localizar el elemento target y observar cambios de layout + apariciones tardías.
  // El target de un paso puede no existir al activarse (p.ej. el drawer createKlon se
  // monta al pulsar "Nuevo Klon"): un MutationObserver re-ejecuta la búsqueda cuando el
  // DOM cambia y reposiciona el spotlight (D3). El observer NO avanza el paso — solo
  // reposiciona; avance y observación quedan desacoplados.
  useLayoutEffect(() => {
    if (!open || !currentStep) return;
    if (!currentStep.target) {
      setTargetRect(null);
      return;
    }

    // `update`: mide la posición del target y lo reenfoca. Lo usan los observers y listeners
    // durante todo el paso para reposicionar el spotlight ante cambios de layout.
    const update = () => {
      const el = document.querySelector(currentStep.target!) as HTMLElement | null;
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        // Scroll instantáneo (no 'smooth'): el scroll suave cambia la posición del target
        // durante cientos de ms y descuadra el spotlight hasta que termina.
        // scrollIntoView puede no existir en algunos entornos (jsdom) — guard defensivo.
        el.scrollIntoView?.({ behavior: 'auto', block: 'center' });
        resizeObserver.observe(el);
      } else {
        setTargetRect(null);
      }
    };

    // MutationObserver: el target aparece/desaparece (drawer que se monta tarde — D3).
    const observer = new MutationObserver(update);

    // ResizeObserver: el target o el layout cambian de TAMAÑO sin mutación de DOM (panel que
    // colapsa, drawer que cierra). Observamos document.body para reflows globales durante el paso.
    const resizeObserver = new ResizeObserver(update);

    update();
    observer.observe(document.body, { childList: true, subtree: true });
    resizeObserver.observe(document.body);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      observer.disconnect(); // E69 — cleanup obligatorio del MutationObserver.
      resizeObserver.disconnect(); // E69 — cleanup obligatorio del ResizeObserver.
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, currentStep]);

  // Listener delegado en `document` (fase capture) para pasos `advanceOn` (D2).
  // Cuando el paso activo es interactivo, el tour avanza al hacer click sobre el
  // elemento observado (`advanceOn.selector` o, por defecto, el `target` del paso).
  // Está activo todo el paso: no depende de que el target exista aún (el drawer
  // createKlon se monta tarde) — el click se captura cuando el elemento aparezca.
  // El avance se hace con un updater de `setStepIndex` (no con `handleNext`, que se
  // declara tras el early-return) para mantener el handler estable y desacoplado.
  useEffect(() => {
    if (!open || !currentStep?.advanceOn) return;
    const matchSelector = currentStep.advanceOn.selector ?? currentStep.target;
    if (!matchSelector) return;

    const handler = (e: MouseEvent) => {
      const el = e.target as Element | null;
      if (el?.closest(matchSelector)) {
        setStepIndex(i => Math.min(steps.length - 1, i + 1));
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true); // E73 — cleanup obligatorio.
  }, [open, currentStep, steps.length]);

  // Animar el cutout desde el estado actual → rect del target con easeOutCubic.
  // El popover lee `animatedRect`, así que se reposiciona en sincronía con el cutout.
  useEffect(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (!targetRect) {
      setAnimatedRect(null);
      return;
    }

    const target: SimpleRect = {
      left: targetRect.left,
      top: targetRect.top,
      width: targetRect.width,
      height: targetRect.height,
    };

    const start: SimpleRect = animatedRect ?? {
      left: target.left + target.width / 2,
      top: target.top + target.height / 2,
      width: 0,
      height: 0,
    };

    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / CUTOUT_ANIMATION_MS);
      const eased = easeOutCubic(t);

      setAnimatedRect({
        left: start.left + (target.left - start.left) * eased,
        top: start.top + (target.top - start.top) * eased,
        width: start.width + (target.width - start.width) * eased,
        height: start.height + (target.height - start.height) * eased,
      });

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRect?.left, targetRect?.top, targetRect?.width, targetRect?.height]);

  if (!open || !currentStep) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const handlePrev = () => setStepIndex(i => Math.max(0, i - 1));
  const handleNext = () => {
    // En pasos advanceOn, "Siguiente" hace lo mismo que pulsar el botón real: dispara un click
    // programático sobre el target (o advanceOn.selector), que abre el panel / modo edición y
    // hace avanzar el tour vía el listener de captura. Así el usuario puede usar el botón real
    // O "Siguiente" indistintamente, y nunca avanzamos a un paso cuyo resaltado aún no existe.
    // Si el elemento no está en el DOM, avanzamos igualmente para no dejar el tour atascado.
    if (currentStep?.advanceOn) {
      const matchSelector = currentStep.advanceOn.selector ?? currentStep.target;
      const realEl = matchSelector
        ? (document.querySelector(matchSelector) as HTMLElement | null)
        : null;
      if (realEl) {
        realEl.click(); // el listener advanceOn (captura) avanza el paso
        return;
      }
    }
    setStepIndex(i => Math.min(steps.length - 1, i + 1));
  };
  // Finalizar el tour ("¡Listo!" en el último paso) → completado (check verde).
  const handleDone = () => {
    setStepIndex(0);
    onClose('completed');
  };
  // Cerrar a medias (X del popover, click en el overlay en paso lineal) → visto (sin check).
  const handleDismiss = () => {
    setStepIndex(0);
    onClose('dismissed');
  };

  return createPortal(
    <div className="klonup-tour-root">
      {/* Click en el overlay = cerrar a medias (salvo en pasos advanceOn — D5). */}
      <TourOverlay rect={animatedRect} onClick={handleDismiss} isAdvanceOn={!!currentStep.advanceOn} />

      <TourPopover
        key={currentStep.id}
        targetRect={
          targetRect
            ? {
                left: targetRect.left,
                top: targetRect.top,
                width: targetRect.width,
                height: targetRect.height,
              }
            : null
        }
        step={currentStep}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        isFirst={isFirst}
        isLast={isLast}
        labels={labels}
        onPrev={handlePrev}
        onNext={handleNext}
        onDone={handleDone}
        onClose={handleDismiss}
      />
    </div>,
    document.body
  );
}
